import React, { useEffect, useMemo, useRef, useState } from 'react';
import { APIProvider, useMapsLibrary } from '@vis.gl/react-google-maps';
import { MapPin, Search, X, Locate, ChevronDown } from 'lucide-react';

/**
 * LocationSelector — iter127
 *
 * A tiny, dropdown-style manual location picker. Renders a clickable pill
 * showing the current locality; when the user taps it, a search-driven
 * autocomplete opens. English-only Places results are enforced via the
 * `language="en"` prop on APIProvider so the Hindi flash observed elsewhere
 * cannot happen here either. Optionally exposes a "Use my location" button
 * that fires the parent-supplied `onDetect` callback (typically triggering
 * the same watchPosition path used by HeaderLocalityDetector).
 */

// Extract a clean short label ("Amanora", "Hadapsar, Pune", "Mumbai") from a
// Places Autocomplete result. Falls back to formatted_address / name.
const shortLabel = (place) => {
  if (!place) return '';
  const comps = place.address_components || [];
  const pick = (types) => (comps.find((c) => types.some((t) => c.types.includes(t))) || {}).long_name;
  return (
    pick(['sublocality_level_1', 'sublocality', 'neighborhood']) ||
    pick(['locality']) ||
    place.name ||
    place.formatted_address ||
    ''
  );
};

const AutocompleteBox = ({ onPick }) => {
  const placesLib = useMapsLibrary('places');
  const inputRef = useRef(null);
  const [autocomplete, setAutocomplete] = useState(null);

  useEffect(() => {
    if (!placesLib || !inputRef.current) return;
    const ac = new placesLib.Autocomplete(inputRef.current, {
      fields: ['geometry', 'formatted_address', 'address_components', 'name'],
      componentRestrictions: { country: 'in' },
      types: ['(regions)'],
    });
    setAutocomplete(ac);
  }, [placesLib]);

  useEffect(() => {
    if (!autocomplete) return;
    const listener = autocomplete.addListener('place_changed', () => {
      const p = autocomplete.getPlace();
      const label = shortLabel(p);
      if (label) onPick(label);
    });
    // Focus once ready so the keyboard opens immediately on mobile.
    setTimeout(() => { try { inputRef.current?.focus(); } catch (_) { /* noop */ } }, 60);
    return () => listener.remove?.();
  }, [autocomplete, onPick]);

  return (
    <div className="relative">
      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#1800ad] pointer-events-none" />
      <input
        ref={inputRef}
        data-testid="location-selector-input"
        type="text"
        placeholder="Search area, city or PIN (India)"
        className="w-full rounded-xl border border-gray-200 bg-white px-9 py-2.5 text-sm text-[#0A0B10] outline-none focus:ring-4 focus:ring-[#1800ad]/10"
      />
    </div>
  );
};

export const LocationSelector = ({ currentLabel, onSelect, onDetect, className = '' }) => {
  const [open, setOpen] = useState(false);
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
  const displayLabel = currentLabel || 'Set your location';
  const wrapperRef = useRef(null);

  // Close on outside click / Escape. iter129 — CRITICAL FIX: the Google Places
  // Autocomplete dropdown (`.pac-container`) is portalled to `document.body`.
  // Clicks on suggestions therefore fall OUTSIDE our wrapper, which used to
  // close the panel before the click reached the suggestion — so the user
  // typed "Mumbai" but tapping the match never set the location. Now we
  // whitelist `.pac-container` from outside-click detection.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      const tgt = e.target;
      // Ignore clicks landing inside the Places Autocomplete portal.
      if (tgt && tgt.closest && tgt.closest('.pac-container')) return;
      if (wrapperRef.current && !wrapperRef.current.contains(tgt)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown, { passive: true });
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handlePick = useMemo(() => (label) => {
    onSelect?.(label);
    setOpen(false);
  }, [onSelect]);

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <button
        type="button"
        data-testid="location-selector-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label="Change location"
        className="group inline-flex items-center gap-1.5 pl-2 pr-2.5 py-1 rounded-full text-[12px] sm:text-sm text-[#0A0B10] font-semibold bg-[#f0edff]/60 hover:bg-[#f0edff] border border-[#1800ad]/15 transition-colors"
      >
        <MapPin size={15} className="text-[#ff8500]" />
        <span className="truncate max-w-[150px] sm:max-w-[220px]" data-testid="location-selector-label">{displayLabel}</span>
        <ChevronDown
          size={14}
          strokeWidth={2.5}
          className={`text-[#1800ad] transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div
          data-testid="location-selector-panel"
          className="absolute z-50 mt-2 left-0 w-[280px] sm:w-[320px] rounded-2xl border border-gray-200 bg-white shadow-xl p-3"
          role="dialog"
        >
          <div className="flex items-center justify-between mb-2 px-1">
            <p className="text-xs font-bold text-[#0A0B10] uppercase tracking-wider">Change location</p>
            <button
              type="button"
              data-testid="location-selector-close"
              onClick={() => setOpen(false)}
              className="text-[#6B7280] hover:text-[#0A0B10]"
            >
              <X size={16} />
            </button>
          </div>
          {apiKey ? (
            <APIProvider apiKey={apiKey} libraries={['places']} language="en" region="IN">
              <AutocompleteBox onPick={handlePick} />
            </APIProvider>
          ) : (
            <p className="text-xs text-[#9CA3AF] px-1 py-2">Search is unavailable — location key missing.</p>
          )}
          {onDetect && (
            <button
              type="button"
              data-testid="location-selector-detect-btn"
              onClick={() => { onDetect?.(); setOpen(false); }}
              className="mt-2 w-full inline-flex items-center justify-center gap-1.5 rounded-xl bg-[#f0edff] text-[#1800ad] py-2 text-xs font-bold hover:bg-[#e2dcff] transition-colors"
            >
              <Locate size={14} /> Use my current location
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default LocationSelector;
